import React, { useContext, CSSProperties, useState, useCallback, useEffect } from 'react';
import {
    Clear as ClearIcon,
    Delete as DeleteIcon,
    Notifications as NotificationsIcon,
    Refresh as RefreshIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
} from '@material-ui/icons';
import { Button, ListItem, makeStyles, Menu, Tooltip } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { useHistory } from 'react-router-dom';
import LocalizationContext from '../../contexts/LocalizationContext';
import INotification from '../../interfaces/INotification';
import format from '../../utilities/format';
import ConfirmPopup from './ConfirmPopup';
import { AccountContext } from '../apps/account/context/AccountProvider';

import dayjs from 'dayjs';

const useStyles = makeStyles({
    notificationsList: {
        minWidth: '450px',
    },
    seenIcon: {
        fontSize: '16px',
        margin: 'auto',
    },
    seenButton: {
        minWidth: 'unset',
        margin: 'auto',
        padding: '6px',
    },
    listTitle: {
        fontWeight: 600,
        paddingRight: '12px',
    },
    notifsCount: {
        color: 'white',
        backgroundColor: '#c0392b',
        position: 'absolute',
        top: 0,
        right: 0,
        width: '20px',
        height: '20px',
        borderRadius: '20px',
        lineHeight: '20px',
        textAlign: 'center',
    },
    nothingNew: {
        fontSize: '15px',
        opacity: '0.6',
        minWidth: '320px',
    },
    notificationDate: {
        color: '#aaa',
        fontStyle: 'italic',
        marginRight: '6px',
        marginTop: '-2px',
        marginBottom: '2px',
    },
});

const refreshAfterSeconds: number = 300;

interface NotificationsProps {
    className: string;
    style?: CSSProperties;
}

const Notifications = ({ className, style }: NotificationsProps) => {
    const [anchorEl, setAnchorEl] = useState<any>(null);
    const classes = useStyles();
    const {
        notificationsAndToken: { notifications },
    } = useContext(AccountContext);

    return (
        <div className={className} style={style}>
            <Button
                onClick={(e) => setAnchorEl(e.currentTarget)}
                style={{ minWidth: 'unset', width: '40px', height: '40px', margin: 'auto' }}>
                {notifications.length > 0 && (
                    <div className={classes.notifsCount}>{notifications.filter((notif: INotification) => !notif.seen).length}</div>
                )}
                <NotificationsIcon />
            </Button>
            <NotificationsList open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={() => setAnchorEl(null)} />
        </div>
    );
};

interface NotificationsListProps {
    open: boolean;
    anchorEl: any;
    onClose(): void;
}

const NotificationsList = ({ open, anchorEl, onClose }: NotificationsListProps) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [openExpirationDialog, setOpenExpirationDialog] = useState<boolean>(false);
    const [acknoledgedExpiration, setAcknoledgedExpiration] = useState<boolean>(false);
    const [intervalId, setIntervalId] = useState<NodeJS.Timeout | null>(null);
    const localization = useContext(LocalizationContext);
    const history = useHistory();
    const classes = useStyles();
    const {
        getNotification,
        notificationsAndToken: { expirationMinutes, notifications, showExpirationNotification },
        markNotifAsSeen,
        deleteNotif,
    } = useContext(AccountContext);

    const loadNotifs = useCallback(
        (showLoading: boolean, initialLoad: boolean) => {
            if ((window as any).disconnected) return;
            if (showLoading) setLoading(true);
            getNotification(
                () => {
                    if (showLoading) setLoading(false);
                },
                () => {
                    if (showLoading) setLoading(false);
                    if (!initialLoad) {
                        alert(localization.strings.notifications.loadingError);
                        (window as any).disconnected = true;
                    }
                }
            );
        },
        [getNotification, localization.strings.notifications.loadingError]
    );

    useEffect(() => {
        if (openExpirationDialog) return;
        // show modal when under 10 mins, and also when it's under 5 mins
        if (showExpirationNotification && ((expirationMinutes <= 10 && expirationMinutes > 5) || expirationMinutes <= 5)) {
            setOpenExpirationDialog(true);
            setAcknoledgedExpiration(false);
        }
    }, [expirationMinutes, openExpirationDialog, showExpirationNotification]);

    useEffect(() => {
        if (intervalId === null) {
            loadNotifs(true, true);
            setIntervalId(setInterval(() => loadNotifs(false, false), refreshAfterSeconds * 1000));
        }
    }, [intervalId, setIntervalId, loadNotifs]);

    const markNotifAs = useCallback(
        (id: number, seen: boolean): void => {
            markNotifAsSeen(
                id,
                seen,
                () => {},
                () => {
                    alert(localization.strings.notifications.markAsSeenWarning);
                }
            );
        },
        [markNotifAsSeen, localization.strings.notifications.markAsSeenWarning]
    );

    const handleDeleteNotif = useCallback(
        (id: number | undefined) => {
            deleteNotif(
                id,
                () => {},
                () => {
                    alert(localization.strings.notifications.deleteWarning);
                }
            );
        },
        [deleteNotif, localization.strings.notifications.deleteWarning]
    );

    return (
        <>
            <ConfirmPopup
                confirmText={localization.strings.ok}
                title={localization.strings.sessionAboutToExpire}
                content=''
                open={openExpirationDialog && !acknoledgedExpiration}
                allowCancel={false}
                onConfirm={() => {
                    setAcknoledgedExpiration(true);
                }}
            />
            <Menu
                open={open}
                onClose={onClose}
                keepMounted
                anchorEl={anchorEl}
                style={{ marginTop: '40px', maxWidth: '50%' }}
                className='notifs-panel'>
                <ListItem className={classes.listTitle}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', margin: '-4px -4px -4px 0' }}>
                        <div style={{ flexGrow: 1, margin: 'auto 0' }}>{localization.strings.notification}</div>
                        <Tooltip title={localization.strings.refresh}>
                            <Button style={{ minWidth: 'unset', margin: 'auto' }} onClick={() => loadNotifs(true, false)}>
                                <RefreshIcon style={{ fontSize: '20px' }} />
                            </Button>
                        </Tooltip>
                        <Tooltip title={localization.strings.clearAll}>
                            <Button style={{ minWidth: 'unset', margin: 'auto' }} onClick={() => handleDeleteNotif(undefined)}>
                                <DeleteIcon style={{ fontSize: '20px' }} />
                            </Button>
                        </Tooltip>
                    </div>
                </ListItem>
                {loading ? (
                    <NotificationSkeleton />
                ) : notifications.length > 0 ? (
                    notifications
                        .sort((a, b) => (a.date === b.date ? 0 : a.date > b.date ? -1 : 1))
                        .map((notif) => (
                            <NotificationItem
                                key={notif.id}
                                {...notif}
                                onDeleteClicked={() => handleDeleteNotif(notif.id)}
                                onMarkAsClicked={() => {
                                    markNotifAs(notif.id, !notif.seen);
                                }}
                                onNotificationOpened={() => {
                                    history.push(notif.url);
                                    if (!notif.seen) markNotifAs(notif.id, true);
                                    onClose();
                                }}
                            />
                        ))
                ) : (
                    <ListItem className={classes.nothingNew}>{localization.strings.nothingNew}</ListItem>
                )}
            </Menu>
        </>
    );
};

interface NotificationItemProps extends INotification {
    onNotificationOpened(): void;
    onMarkAsClicked(): void;
    onDeleteClicked(): void;
}

const NotificationItem = ({
    onNotificationOpened,
    titleResourceName,
    bodyResourceName,
    payload,
    date,
    seen,
    onMarkAsClicked,
    onDeleteClicked,
}: NotificationItemProps) => {
    const localization = useContext(LocalizationContext);
    const classes = useStyles();

    return (
        <ListItem button onClick={onNotificationOpened} className={classes.notificationsList} style={{ opacity: seen ? 0.6 : 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                    <div style={{ fontSize: '15px', fontWeight: seen ? 400 : 600 }}>{localization.strings.notifs[titleResourceName]}</div>
                    <div style={{ fontSize: '13px' }}>
                        <div className={classes.notificationDate}>
                            {`${dayjs.utc(date).local().format('YYYY-MM-DD HH:mm:ss')}`}
                        </div>
                        {format(localization.strings.notifs?.[bodyResourceName], payload)}
                    </div>
                </div>
                <Tooltip title={`${localization.strings.markAs} ${seen ? localization.strings.unseen : localization.strings.seen}`}>
                    <Button
                        className={classes.seenButton}
                        onClick={(e) => {
                            onMarkAsClicked();
                            e.stopPropagation();
                        }}>
                        {seen ? <VisibilityOffIcon className={classes.seenIcon} /> : <VisibilityIcon className={classes.seenIcon} />}
                    </Button>
                </Tooltip>
                <Tooltip title={localization.strings.delete}>
                    <Button
                        className={classes.seenButton}
                        onClick={(e) => {
                            onDeleteClicked();
                            e.stopPropagation();
                        }}>
                        <ClearIcon className={classes.seenIcon} />
                    </Button>
                </Tooltip>
            </div>
        </ListItem>
    );
};

const NotificationSkeleton = () => {
    const classes = useStyles();

    return (
        <ListItem className={classes.notificationsList}>
            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                    <Skeleton variant='text' style={{ width: '50%' }} />
                    <Skeleton variant='text' style={{ height: '14px' }} />
                </div>
                <Skeleton variant='circle' style={{ width: '14px', height: '14px', margin: 'auto 10px' }} />
            </div>
        </ListItem>
    );
};

export default Notifications;
